import {
  ErrorResponse,
  OidcClient,
  type SigninResponse,
  type SigninSilentArgs,
  User,
  type UseRefreshTokenArgs,
  UserManager,
  type UserManagerSettings,
} from "oidc-client-ts";

import {
  authMetadataKey,
  type AuthSnapshot,
  canonicalUserKey,
  notifyAuthOwner,
  readAuthMetadata,
  readAuthSnapshot,
  writeAuthMetadata,
} from "./auth-owner";
import { authConfig } from "./oidc-config";

export const accessTokenExpiringLeewaySeconds = 60;
const exchangeLock = "istari-token-refresh";
const commitLock = "istari-auth-commit";
let localCommit: Promise<unknown> = Promise.resolve();
let localExchange: Promise<unknown> = Promise.resolve();
let instance: GuardedUserManager | undefined;

export function getUserManager(): GuardedUserManager {
  instance ??= new GuardedUserManager(toUserManagerSettings());
  return instance;
}

/** Resolves after any live commit, recovering an abandoned transition without exporting credentials. */
export function waitForAuthReady(): Promise<void> {
  return getUserManager().initialize();
}

/** Waits for credential readiness without adopting a replacement session. */
export async function waitForAuthOwnerReady(owner: string | undefined): Promise<boolean> {
  if (owner === undefined) return false;
  try {
    await waitForAuthReady();
    const snapshot = readAuthSnapshot();
    return snapshot?.user !== null && snapshot?.metadata.owner === owner;
  } catch {
    return false;
  }
}

export async function removeAuthOwner(owner: string | undefined): Promise<void> {
  if (owner === undefined) return;
  await getUserManager().removeOwner(owner);
}

export class GuardedUserManager extends UserManager {
  private readonly validationClient: OidcClient;

  constructor(settings: UserManagerSettings) {
    super({ ...settings, automaticSilentRenew: false });
    this.validationClient = new OidcClient(settings);
  }

  async initialize(): Promise<void> {
    await this.withCommit(() => this.recover());
  }

  override async getUser(raiseEvent = false): Promise<User | null> {
    await this.initialize();
    await this.withCommit(async () => {
      const user = readAuthSnapshot()?.user ?? null;
      if (user) await this.events.load(user, raiseEvent);
    });
    return readAuthSnapshot()?.user ?? null;
  }

  override async storeUser(): Promise<void> {
    throw new Error("Direct OIDC storage writes are unsupported; use sign-in or removeUser.");
  }

  override async revokeTokens(): Promise<void> {
    throw new Error("Token revocation is unsupported by the shared session manager.");
  }

  protected override async _revokeInternal(): Promise<void> {
    throw new Error("Token revocation is unsupported by the shared session manager.");
  }

  protected override async _loadUser(): Promise<User | null> {
    return readAuthSnapshot()?.user ?? null;
  }

  override async removeUser(): Promise<void> {
    await this.withCommit(async () => {
      await this.recover();
      await this.commit(null, crypto.randomUUID());
    });
  }

  async removeOwner(owner: string): Promise<void> {
    await this.withCommit(async () => {
      const current = readAuthSnapshot();
      if (current?.metadata.owner === owner) await this.commit(null, crypto.randomUUID());
    });
  }

  async removeUserIfCurrent(expected: AuthSnapshot): Promise<void> {
    await this.withCommit(async () => {
      if (this.matches(expected)) await this.commit(null, crypto.randomUUID());
    });
  }

  override async signinSilent(args: SigninSilentArgs = {}): Promise<User | null> {
    if (args.forceIframeAuth) throw new Error("Iframe renewal is unsupported by the shared session manager.");
    return await this.renew({ args });
  }

  async renew(options: {
    leewaySeconds?: number;
    rejectedToken?: string;
    owner?: string;
    args?: SigninSilentArgs;
  }): Promise<User | null> {
    const run = async () => {
      if (options.rejectedToken !== undefined && !hasWebLocks()) return null;
      return await withWebLock(exchangeLock, async () => {
        await this.initialize();
        const snapshot = readAuthSnapshot();
        if (!snapshot?.user || (options.owner !== undefined && snapshot.metadata.owner !== options.owner)) return null;
        const user = snapshot.user;
        if (options.rejectedToken !== undefined && user.access_token !== options.rejectedToken) return user;
        if (
          options.leewaySeconds !== undefined &&
          (!user.expires_at || Date.now() <= (user.expires_at - options.leewaySeconds) * 1000)
        ) {
          return user;
        }
        if (!user.refresh_token) {
          await this.removeUserIfCurrent(snapshot);
          return null;
        }
        try {
          return await this.exchange(snapshot, {
            // RefreshState is declared but not exported by the installed runtime.
            state: {
              refresh_token: user.refresh_token,
              id_token: user.id_token,
              session_state: user.session_state,
              scope: user.scope,
              profile: user.profile,
              data: user.state,
            },
            redirect_uri: options.args?.redirect_uri,
            resource: options.args?.resource,
            extraTokenParams: options.args?.extraTokenParams,
            timeoutInSeconds: options.args?.silentRequestTimeoutInSeconds,
          });
        } catch (error) {
          if (isInvalidGrant(error)) await this.removeUserIfCurrent(snapshot);
          throw error;
        }
      });
    };
    const pending = localExchange.catch(() => undefined).then(run);
    localExchange = pending;
    return await pending;
  }

  protected override async _useRefreshToken(_args: UseRefreshTokenArgs): Promise<User> {
    const user = await this.renew({});
    if (!user) throw new Error("Session is unavailable.");
    return user;
  }

  protected override async _buildUser(response: SigninResponse, verifySub?: string): Promise<User> {
    const user = new User(response);
    if (verifySub && verifySub !== user.profile.sub) throw new ErrorResponse({ ...response, error: "login_required" });
    await this.withCommit(async () => {
      await this.recover();
      await this.commit(user, crypto.randomUUID());
    });
    return user;
  }

  private async exchange(snapshot: AuthSnapshot, args: UseRefreshTokenArgs): Promise<User | null> {
    const response = await this.validationClient.useRefreshToken({
      timeoutInSeconds: this.settings.silentRequestTimeoutInSeconds,
      ...args,
    });
    const user = new User({ ...args.state, ...response });
    return await this.withCommit(async () => {
      if (!this.matches(snapshot)) return null;
      await this.commit(user, snapshot.metadata.owner);
      return user;
    });
  }

  private matches(expected: AuthSnapshot): boolean {
    const current = readAuthSnapshot();
    return (
      current !== undefined &&
      current.metadata.owner === expected.metadata.owner &&
      current.metadata.revision === expected.metadata.revision
    );
  }

  private async commit(user: User | null, owner: string): Promise<void> {
    const previous = readAuthMetadata();
    const previousOwner = previous?.state === "stable" ? previous.owner : undefined;
    const revision = crypto.randomUUID();
    writeAuthMetadata({
      state: "transitioning",
      transitionId: crypto.randomUUID(),
      previousOwner,
      nextOwner: owner,
      nextRevision: revision,
    });
    if (previousOwner !== owner) notifyAuthOwner();
    try {
      await super.storeUser(user);
      writeAuthMetadata({ state: "stable", owner, revision, hasUser: user !== null });
    } catch (error) {
      const metadata = readAuthMetadata();
      if (metadata?.state === "transitioning") {
        try {
          writeAuthMetadata({ ...metadata, unavailable: true });
        } catch {
          // Recovery under the commit lock remains mandatory when storage is unavailable.
        }
      }
      notifyAuthOwner();
      throw error;
    }
    if (user) await this.events.load(user);
    else await this.events.unload();
    notifyAuthOwner();
  }

  private async recover(): Promise<void> {
    const metadata = readAuthMetadata();
    if (metadata?.state === "transitioning" || (metadata?.state === "stable" && !readAuthSnapshot())) {
      await this.commit(null, crypto.randomUUID());
      return;
    }
    if (metadata) return;
    if (window.localStorage.getItem(authMetadataKey) !== null) {
      await this.commit(null, crypto.randomUUID());
      return;
    }
    // Initialization and legacy migration are serialized with all canonical writers.
    const raw = window.localStorage.getItem(canonicalUserKey);
    if (raw) {
      try {
        User.fromStorageString(raw);
      } catch {
        await this.commit(null, crypto.randomUUID());
        return;
      }
    }
    writeAuthMetadata({
      state: "stable",
      owner: crypto.randomUUID(),
      revision: crypto.randomUUID(),
      hasUser: raw !== null,
    });
    notifyAuthOwner();
  }

  private async withCommit<T>(callback: () => Promise<T>): Promise<T> {
    const pending = localCommit.catch(() => undefined).then(() => withWebLock(commitLock, callback));
    localCommit = pending;
    return await pending;
  }
}

function hasWebLocks(): boolean {
  return typeof navigator !== "undefined" && navigator.locks != null;
}

async function withWebLock<T>(name: string, callback: () => Promise<T>): Promise<T> {
  if (!hasWebLocks()) return await callback();
  return await navigator.locks.request(name, { signal: AbortSignal.timeout(15_000) }, callback);
}

function isInvalidGrant(error: unknown): boolean {
  return typeof error === "object" && error !== null && "error" in error && error.error === "invalid_grant";
}

function toUserManagerSettings(): UserManagerSettings {
  const { authority, client_id, redirect_uri } = authConfig;
  if (!authority || !client_id || !redirect_uri) {
    throw new Error("Auth configuration is incomplete: authority, client_id and redirect_uri are required.");
  }
  return {
    ...authConfig,
    authority,
    client_id,
    redirect_uri,
    accessTokenExpiringNotificationTimeInSeconds: accessTokenExpiringLeewaySeconds,
    automaticSilentRenew: false,
  };
}
